import React, { Component, Dispatch } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router';
import { SofaButton, SofaPanel } from '@sofology/sofologycomponents';

import './FinanceApplicationStepThreePage.scss';

import { FinanceBackButton, Loader, StepBar } from 'components';
import { scrollHelper, generateDispatchToPropsMap, GtmCheckoutSteps, logsHelper, errorToBasketErrorModel } from 'helpers';
import { preprocessRedirectToNextStep, PreprocessRedirectToNextStepFunc } from 'store/checkout/actions';
import { AppState } from 'store';
import { EmploymentDetailsForm } from './';
import { EmploymentDetailsFormType } from './EmploymentDetailsForm/EmploymentDetailsForm';
import { saveIfcFinanceData, SaveIfcFinanceDataFunc } from 'store/checkout/actions/finance';
import { routes } from 'routes';
import { IlpQuestionDescriptions, IfcApplicationData } from 'models';

interface OwnState {
    isContinueClickProcessing: boolean;
    isContinueButtonDisabled: boolean;
}

interface StateProps {
    firstFinanceApplicationPageInitialized: boolean;
    ifcQuestionSet: IlpQuestionDescriptions | null;
    ifcApplicationData: IfcApplicationData | null;
}

interface DispatchProps {
    saveIfcFinanceData: SaveIfcFinanceDataFunc;
    preprocessRedirectToNextStep: PreprocessRedirectToNextStepFunc;
}

const CURRENT_STEP = 3;
const STEPS_COUNT = 4;

class FinanceApplicationStepThreePage extends Component<StateProps & DispatchProps & RouteComponentProps, OwnState> {
    private employmentDetailsForm = React.createRef<EmploymentDetailsFormType>();

    constructor(props: StateProps & DispatchProps & RouteComponentProps) {
        super(props);

        this.state = {
            isContinueClickProcessing: false,
            isContinueButtonDisabled: false
        };
    }

    componentDidMount() {
        if (!this.props.firstFinanceApplicationPageInitialized) {
            this.props.history.push(routes.financeApplication.path);
        }
    }

    handleContinueClick = () => {
        this.setState({ isContinueClickProcessing: true }, () => {
            this.validateForm()
                .then(isValid => {
                    if (isValid) {
                        const formComponent = this.employmentDetailsForm.current!;
                        const values = formComponent.getFormData();

                        this.props.saveIfcFinanceData(values);

                        this.props.preprocessRedirectToNextStep(
                            () => this.props.history.push(routes.financeApplicationStepFour.path),
                            GtmCheckoutSteps.FinanceFormStepFour
                        );
                    } else {
                        this.setState({ isContinueClickProcessing: false });
                    }
                })
                .catch(error => {
                    logsHelper.error(errorToBasketErrorModel(error, 'FinanceApplicationStepThreePage:handleContinueClick'));
                    this.setState({ isContinueClickProcessing: false });
                });
        });
    };

    handleBackButtonClick = async () => {
        const values = this.employmentDetailsForm.current!.getFormData();

        this.props.saveIfcFinanceData(values);
        this.props.history.push(routes.financeApplicationStepTwo.path);
    };

    handleGoToStartCheckout = () => {
        this.props.history.push(routes.deliveryDetails.path);
    };

    handleDynamicFormInitialized = () => {
        this.setState({ isContinueButtonDisabled: this.employmentDetailsForm.current!.hasInvalidReadonlyControls() });
    };

    render(): JSX.Element {
        const { firstFinanceApplicationPageInitialized } = this.props;

        return (
            <div className='finance-application-step-three'>
                {
                    firstFinanceApplicationPageInitialized
                        ? this.renderContent()
                        : <Loader show={true} />
                }
            </div>
        );
    }

    renderContent(): JSX.Element {
        return (
            <>
                <FinanceBackButton onClick={this.handleBackButtonClick} />
                <StepBar stepsCount={STEPS_COUNT} currentStep={CURRENT_STEP} />
                <SofaPanel>
                    <EmploymentDetailsForm
                        ref={this.employmentDetailsForm}
                        ifcApplicationData={this.props.ifcApplicationData!}
                        ifcQuestionSet={this.props.ifcQuestionSet!}
                        onGoToStartCheckout={this.handleGoToStartCheckout}
                        onDynamicFormInitialized={this.handleDynamicFormInitialized}
                    />
                </SofaPanel>
                {this.renderContinueButton()}
            </>
        );
    }

    renderContinueButton(): JSX.Element {
        return (
            <div className='finance-application-step-two-page__continue-button'>
                {this.state.isContinueClickProcessing && <Loader show={true} overlay={true} />}
                <SofaButton
                    text='Continue'
                    variant='primary'
                    disabled={this.state.isContinueButtonDisabled}
                    onClick={this.handleContinueClick}
                />
            </div>
        );
    }

    async validateForm(): Promise<boolean> {
        let isValid = true;
        const formComponent = this.employmentDetailsForm.current!;
        const invalidControls = await formComponent.validate();

        if (invalidControls.length > 0) {
            isValid = false;

            let firstInvalidControl = invalidControls.min(c => c.index);
            firstInvalidControl.ref?.current && scrollHelper.scrollTo(firstInvalidControl.ref?.current.element);
        }

        return isValid;
    }
}

function mapStateToProps(state: AppState): StateProps {
    return {
        firstFinanceApplicationPageInitialized: state.checkoutState.firstFinanceApplicationPageInitialized,
        ifcQuestionSet: state.checkoutState.ifcQuestionSet,
        ifcApplicationData: state.checkoutState.ifcApplicationData
    };
}

function mapDispatchToProps(dispatch: Dispatch<any>): DispatchProps {
    return generateDispatchToPropsMap(dispatch, [saveIfcFinanceData, preprocessRedirectToNextStep]);
}

export default connect(mapStateToProps, mapDispatchToProps, undefined, { forwardRef: true })(FinanceApplicationStepThreePage);